import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import type { DataTableRow } from "@bcc/components/data-table/data-table.types";
import { DEFAULT_ORDER_STATUS, ORDER_STATUS_FILTERS } from "@bcc/components/orders/constants";
import { CreateBatchDialog } from "@bcc/components/orders/create-batch-dialog/create-batch-dialog";
import { OrderDetailDialog } from "@bcc/components/orders/order-detail-dialog/order-detail-dialog";
import { OrdersDataTable } from "@bcc/components/orders/orders-data-table/orders-data-table";
import { OrdersFilterBar } from "@bcc/components/orders/orders-filter-bar/orders-filter-bar";
import { OrdersHeader } from "@bcc/components/orders/orders-header";
import { useToast } from "@bcc/hooks/use-toast";
import type { PurchaseOrder, PurchaseOrderStatus } from "@shared/types";

import { ROUTE_PATHS } from "@bcc/lib/constants";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { setOrdersViewTestId } from "./orders-view.utils";

const ALL_STATUSES_PARAM = [...(ORDER_STATUS_FILTERS.find((filter) => filter.key === "all")?.statuses ?? [])].join(",");

interface OrdersViewProps {
  readonly orders: DataTableRow<PurchaseOrder>[];
  readonly purchaseOrder?: PurchaseOrder | null;
  readonly isLoading?: boolean;
  readonly clearCache?: () => void;
  readonly refetchBatches?: () => Promise<unknown>;
  readonly onDeselectOrder?: () => void;
  readonly statusCounts?: Record<string, number | undefined>;
  readonly isStatusCountsLoading?: boolean;
  readonly purchaseOrderId?: string;
  readonly testId?: string;
}

export function OrdersView({
  orders,
  purchaseOrder = null,
  isLoading = false,
  clearCache,
  refetchBatches,
  onDeselectOrder,
  statusCounts,
  isStatusCountsLoading = false,
  purchaseOrderId,
  testId,
}: OrdersViewProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { carrierAccountId } = useParams<{ carrierAccountId?: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const dialogOpen = Boolean(purchaseOrderId);
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [selectedOrdersForBatch, setSelectedOrdersForBatch] = useState<PurchaseOrder[]>([]);
  const [isExportToCsv, setExportToCsv] = useState(false);
  const exportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (exportTimerRef.current !== null) {
        clearTimeout(exportTimerRef.current);
      }
    };
  }, []);

  // Derived from URL — empty string means "all"
  const activeStatuses = searchParams.get("orderStatus") ?? "";

  const handleFilterChange = useCallback(
    (statuses: readonly PurchaseOrderStatus[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (statuses.length === 0) {
          next.delete("orderStatus");
        } else {
          next.set("orderStatus", [...statuses].join(","));
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const handleOrderClick = useCallback(
    (order: PurchaseOrder) => {
      const encodedPurchaseOrderId = encodeURIComponent(order.purchaseOrderId);
      const pathname = carrierAccountId
        ? `${ROUTE_PATHS.MARKETPLACE}/${carrierAccountId}/orders/${encodedPurchaseOrderId}`
        : `${ROUTE_PATHS.ORDERS}/${encodedPurchaseOrderId}`;
      const search = searchParams.toString();
      navigate(`${pathname}${search ? "?" + search : ""}`);
    },
    [carrierAccountId, navigate, searchParams]
  );

  const handleDialogClose = useCallback(
    (open: boolean) => {
      if (!open) {
        const pathname = carrierAccountId ? `${ROUTE_PATHS.MARKETPLACE}/${carrierAccountId}/orders` : ROUTE_PATHS.ORDERS;
        const search = searchParams.toString();
        navigate(`${pathname}${search ? "?" + search : ""}`);
        onDeselectOrder?.();
      }
    },
    [carrierAccountId, navigate, onDeselectOrder, searchParams]
  );

  const handleBatchClick = useCallback(
    (batchId: string) => {
      navigate({
        pathname: ROUTE_PATHS.BATCHES,
        search: `?batchId=${batchId}`,
      });
    },
    [navigate]
  );

  const handleCreateBatch = useCallback((batchOrders: PurchaseOrder[]) => {
    setSelectedOrdersForBatch(batchOrders);
    setCreateBatchOpen(true);
  }, []);

  const handleShowSummary = useCallback(() => {
    const pathname = carrierAccountId
      ? `${ROUTE_PATHS.MARKETPLACE}/${carrierAccountId}/orders/waiting-summary`
      : `${ROUTE_PATHS.ORDERS}/waiting-summary`;
    const search = searchParams.toString();
    navigate(`${pathname}${search ? "?" + search : ""}`);
  }, [carrierAccountId, navigate, searchParams]);

  // Selection only applies where open orders are listed: "All" or "Waiting to be fulfilled", so every
  // other filter shows no checkboxes at all. The "All" chip stores
  // every status in the URL (comma-joined) rather than clearing the param, so match both forms.
  const showSelectAllOpenOrders = activeStatuses === "" || activeStatuses === DEFAULT_ORDER_STATUS || activeStatuses === ALL_STATUSES_PARAM;

  const canShowSummary = !isLoading && activeStatuses === DEFAULT_ORDER_STATUS && orders.length > 0;

  const handleBatchCreated = useCallback(
    async (batchId: string) => {
      setSelectedOrdersForBatch([]);
      clearCache?.();
      await refetchBatches?.();
      navigate({
        pathname: ROUTE_PATHS.BATCHES,
        search: `?batchId=${batchId}`,
      });
    },
    [clearCache, refetchBatches, navigate]
  );

  const handleExportCSV = useCallback(() => {
    setExportToCsv(true);
    toast({
      title: t("views.orders.toasts.exportStarted"),
      description: t("views.orders.toasts.exportSuccess", {
        count: orders.length,
      }),
    });
    // Reset after one tick — DataTable reads exportToCSV=true on this render, then false on next
    exportTimerRef.current = setTimeout(() => setExportToCsv(false), 100);
  }, [orders.length, t, toast]);

  return (
    <div data-testid={testId ?? setOrdersViewTestId("container")} className="space-y-6">
      <OrdersHeader
        canExport={!isLoading && orders.length > 0}
        onExportCSV={handleExportCSV}
        canShowSummary={canShowSummary}
        onShowSummary={handleShowSummary}
      />

      <OrdersFilterBar
        activeStatuses={activeStatuses}
        onFilterChange={handleFilterChange}
        onSearch={setSearchQuery}
        searchQuery={searchQuery}
        statusCounts={statusCounts}
        isLoading={isStatusCountsLoading}
      />

      <div data-tour-id={TOUR_IDS.orders.table}>
        <OrdersDataTable
          orders={orders}
          onOrderClick={handleOrderClick}
          onBatchClick={handleBatchClick}
          onCreateBatch={handleCreateBatch}
          isLoading={isLoading}
          globalFilter={searchQuery}
          exportToCSV={isExportToCsv}
          selectable={showSelectAllOpenOrders}
          selectionResetKey={showSelectAllOpenOrders ? undefined : activeStatuses}
        />
      </div>

      <OrderDetailDialog purchaseOrder={purchaseOrder} open={dialogOpen} onOpenChange={handleDialogClose} onBatchClick={handleBatchClick} />

      <CreateBatchDialog
        open={createBatchOpen}
        onOpenChange={setCreateBatchOpen}
        selectedOrders={selectedOrdersForBatch}
        onSuccess={handleBatchCreated}
      />
    </div>
  );
}
